import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { lookupsQuery } from '@/shared/lookups';
import { idempotent } from '@/shared/api/headers';

import { configKeys, type ProductCode } from './queries';

interface Scope {
  bankId: string;
  product: ProductCode;
}

/** Configuration changes are never optimistic; banks, versions and compare results refetch. */
function useConfigMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.all });
      void qc.invalidateQueries({ queryKey: configKeys.banks });
    },
  });
}

export const useCreateDraft = ({ bankId, product }: Scope) =>
  useConfigMutation((baseVersionNo: number) => unwrap(api.POST('/banks/{bankId}/products/{productCode}/versions', { params: { path: { bankId, productCode: product }, header: idempotent() }, body: { baseVersionNo } })));

export const useSaveDraft = ({ bankId, product }: Scope, versionNo: number) =>
  useConfigMutation(({ version, body }: { version: string; body: Schema<'ConfigVersionContent'> }) =>
    unwrap(api.PUT('/banks/{bankId}/products/{productCode}/versions/{versionNo}', { params: { path: { bankId, productCode: product, versionNo }, header: { 'If-Match': version } }, body })),
  );

export const useDiscardDraft = ({ bankId, product }: Scope, versionNo: number) =>
  useConfigMutation(() => unwrap(api.DELETE('/banks/{bankId}/products/{productCode}/versions/{versionNo}', { params: { path: { bankId, productCode: product, versionNo } } })));

export const usePublishVersion = ({ bankId, product }: Scope, versionNo: number) =>
  useConfigMutation((effectiveFrom: string) => unwrap(api.POST('/banks/{bankId}/products/{productCode}/versions/{versionNo}/publish', { params: { path: { bankId, productCode: product, versionNo }, header: idempotent() }, body: { effectiveFrom } })));

export const useRetireVersion = ({ bankId, product }: Scope, versionNo: number) =>
  useConfigMutation((effectiveTo: string) => unwrap(api.POST('/banks/{bankId}/products/{productCode}/versions/{versionNo}/retire', { params: { path: { bankId, productCode: product, versionNo }, header: idempotent() }, body: { effectiveTo } })));

/** Bank-level reference data (not versioned): stage catalogue, employer categories, settings, schemes. */
function useBankMutation<TVars, TResult>(bankId: string, fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.bank(bankId) });
      void qc.invalidateQueries({ queryKey: configKeys.banks });
      // Deal pages, queues and MIS read stage mappings and categories.
      void qc.invalidateQueries({ queryKey: ['deals'] });
      void qc.invalidateQueries({ queryKey: ['mis'] });
    },
  });
}

export const useSaveStages = (bankId: string) => useBankMutation(bankId, (stages: Schema<'BankStageInput'>[]) => unwrap(api.PUT('/banks/{bankId}/stages', { params: { path: { bankId } }, body: stages })));

export const useSaveEmployerCategories = (bankId: string) =>
  useBankMutation(bankId, (categories: Schema<'BankEmployerCategory'>[]) => unwrap(api.PUT('/banks/{bankId}/employer-categories', { params: { path: { bankId } }, body: categories })));

export const useUpdateBankSettings = (bankId: string) =>
  useBankMutation(bankId, (body: { submissionCutoff?: string; liabilityLetterWarningDays?: number | null; calendar?: string }) => unwrap(api.PATCH('/banks/{bankId}/settings', { params: { path: { bankId } }, body })));

export const useCreateScheme = (bankId: string, product: ProductCode) =>
  useBankMutation(bankId, (body: Schema<'SchemeInput'>) => unwrap(api.POST('/banks/{bankId}/products/{productCode}/schemes', { params: { path: { bankId, productCode: product }, header: idempotent() }, body })));

/** Onboarding (Add bank → product → schemes): the bank id is only known after the first call, so it is a variable. */
export function useCreateBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'BankInput'>) => unwrap(api.POST('/banks', { body, params: { header: idempotent() } })),
    onSuccess: (bank) => {
      qc.setQueryData(configKeys.bank(bank.id), bank);
      void qc.invalidateQueries({ queryKey: configKeys.banks, exact: true });
    },
  });
}

/** Adds the product and its first configuration draft (v1), from a published template or empty. */
export function useAddBankProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, body }: { bankId: string; body: Schema<'BankProductInput'> }) => unwrap(api.POST('/banks/{bankId}/products', { params: { path: { bankId }, header: idempotent() }, body })),
    onSuccess: (draft, { bankId }) => {
      qc.setQueryData(configKeys.version(bankId, draft.product, draft.versionNo), draft);
      void qc.invalidateQueries({ queryKey: configKeys.versions(bankId, draft.product) });
      void qc.invalidateQueries({ queryKey: configKeys.banks });
    },
  });
}

export function useCreateSchemeFor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, product, body }: Scope & { body: Schema<'SchemeInput'> }) => unwrap(api.POST('/banks/{bankId}/products/{productCode}/schemes', { params: { path: { bankId, productCode: product }, header: idempotent() }, body })),
    onSuccess: (_s, { bankId, product }) => void qc.invalidateQueries({ queryKey: configKeys.schemes(bankId, product) }),
  });
}

/** Replaces an institution's search aliases (versioned: If-Match). Selectors read them from the lookups. */
export function useUpdateInstitutionAliases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, version, aliases }: { bankId: string; version: string; aliases: string[] }) => unwrap(api.PATCH('/reference/financial-institutions/{bankId}', { params: { path: { bankId }, header: { 'If-Match': version } }, body: { aliases } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: configKeys.institutions });
      void qc.invalidateQueries({ queryKey: lookupsQuery.queryKey });
    },
  });
}
